import Modal from "components/DesignSystem/Modal/Modal";
import { useGetSubscriptionsQuery } from "store/apis/subscriptions";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Subscription } from "types/Models/subscription";
import { useState } from "react";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "components/DesignSystem/ToggleGroup/ToggleGroup";
import { CheckoutModal } from "components/CheckoutModal/CheckoutModal";
import { useModal } from "hooks/useModal";
import Loader from "components/design/loader";
import { BOOKS_PLUS_PLAN } from "constants/subscriptionType";
import { BOOKKEEPING_V2 } from "constants/addons";
import { AddonData } from "store/slices/addons";
import { BooksSubscriptionContent } from "./BooksSubscriptionModalContent";
import { BooksServicesSubscriptionsContent } from "./BooksServicesSubscriptionsContent";

type TaxSubscriptionModalProps = {
  show: boolean;
  onClose: () => void;
  entityId: string;
  groupId: string;
  onManage: (addon: AddonData) => void;
  subscriptionModalTab: string;
  setSubscriptionModalTab: (tab: string) => void;
};

export const BooksSubscriptionModal = ({
  show = true,
  onClose,
  entityId,
  groupId,
  onManage,
  subscriptionModalTab,
  setSubscriptionModalTab,
}: TaxSubscriptionModalProps) => {
  const {
    data: subscriptionPlans,
    isLoading,
    isSuccess,
  } = useGetSubscriptionsQuery({
    entityId,
    groupId,
  });
  const [selectedPlan, setSelectedPlan] = useState<Subscription>(
    subscriptionPlans
      ?.filter(
        (item: Subscription) => item?.subscription_type === BOOKS_PLUS_PLAN
      )
      ?.at(0)!
  );
  const handleClose = () => {
    setSubscriptionModalTab("Software");
    onClose();
  };

  const onChange = (value: string) => {
    setSubscriptionModalTab(value);
  };

  return (
    <Modal.Root open={show} onOpenChange={handleClose}>
      <Modal.Content
        size={subscriptionModalTab === "Software" ? "large" : "xl"}
      >
        <Modal.Header>
          <div className="t-w-full">
            <Modal.Title titleIcon={<Modal.Close />}>
              <div className="">Upgrade Your Plan </div>
            </Modal.Title>
          </div>
        </Modal.Header>
        <Modal.Body>
          <Async.Root
            isEmpty={!subscriptionPlans?.length}
            isLoading={isLoading}
            isSuccess={isSuccess}
          >
            <Async.Empty>
              <Loader />
            </Async.Empty>
            <Async.Success>
              <BooksSubscriptionModalContent
                isSoftware={subscriptionModalTab === "Software"}
                selectedPlanType={subscriptionModalTab}
                setSelectedPlanType={onChange}
                subscription_plans={subscriptionPlans!}
                selectedPlan={
                  selectedPlan ||
                  subscriptionPlans
                    ?.filter(
                      (item: Subscription) =>
                        item?.subscription_type === BOOKS_PLUS_PLAN
                    )
                    ?.at(0)!
                }
                setSelectedPlan={setSelectedPlan}
                entityId={entityId}
                onClose={handleClose}
                onManage={onManage}
              />
            </Async.Success>
          </Async.Root>
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  );
};

const BooksSubscriptionModalContent = ({
  isSoftware,
  selectedPlanType,
  setSelectedPlanType,
  selectedPlan,
  subscription_plans,
  setSelectedPlan,
  entityId,
  onClose,
  onManage,
}: {
  isSoftware: boolean;
  selectedPlanType: string;
  setSelectedPlanType: (value: string) => void;
  subscription_plans: Subscription[];
  selectedPlan: Subscription;
  setSelectedPlan: (plan: Subscription) => void;
  entityId: string;
  onClose: () => void;
  onManage: (addon: AddonData) => void;
}) => {
  const checkoutModal = useModal();
  const softwareStandardActivePlan = subscription_plans?.find(
    (item) =>
      item?.subscription_type === BOOKS_PLUS_PLAN &&
      item?.group_subscription?.length
  );

  const softwareStandardBillingCycle = softwareStandardActivePlan?.billing_cycle
    ? softwareStandardActivePlan?.billing_cycle === "MONTHLY"
      ? "month"
      : "year"
    : selectedPlan?.billing_cycle! === "MONTHLY"
    ? "month"
    : "year";

  const softwareStandardAmount =
    softwareStandardActivePlan?.amount! || selectedPlan?.amount! || 30;

  const softwareStandardButtonText = softwareStandardActivePlan
    ? "Manage Subscription"
    : "Upgrade to Standard";

  const softwareStandardButtonType = softwareStandardActivePlan
    ? "secondary"
    : "primary-outlined";

  const bookkeepingPlusActivePlan = subscription_plans?.find(
    (item) =>
      item?.subscription_type === BOOKKEEPING_V2 &&
      item?.group_subscription?.length
  );

  return (
    <section className="t-flex t-flex-col t-gap-3.5">
      <div className="t-border-neutral-0 t-bg-surface t-mx-auto">
        <ToggleGroup
          value={selectedPlanType}
          onValueChange={(e) => setSelectedPlanType(e)}
        >
          <ToggleGroupItem value="Software">Software</ToggleGroupItem>
          <ToggleGroupItem value="Services">Services</ToggleGroupItem>
        </ToggleGroup>
      </div>
      {isSoftware ? (
        <BooksSubscriptionContent
          price={softwareStandardAmount}
          softwareStandardActivePlan={softwareStandardActivePlan}
          selectedPlan={selectedPlan}
          billingCycle={softwareStandardBillingCycle}
          buttonType={softwareStandardButtonType}
          buttonText={softwareStandardButtonText}
          onManage={onManage}
          subscriptionPlans={subscription_plans}
          setSelectedPlan={setSelectedPlan}
          checkoutModal={checkoutModal}
          toggleValue={selectedPlan?.uuid}
        />
      ) : (
        <BooksServicesSubscriptionsContent
          bookkeepingPlusActivePlan={bookkeepingPlusActivePlan}
          subscriptionPlans={subscription_plans}
          onManage={onManage}
        />
      )}
      <CheckoutModal
        entityId={entityId}
        type="subscription"
        onClose={checkoutModal.close}
        open={checkoutModal.isOpen}
        subscription={selectedPlan!}
        onSubscribed={() => {
          checkoutModal.close();
        }}
      />
    </section>
  );
};
